import { ControllerParams, CreateControllerFn } from '@wix/yoshi-flow-editor';
import { faqAppStartLoading, faqAppLoads } from '@wix/bi-logger-labs-data/v2';
import type { faqAppLoadsParams } from '@wix/bi-logger-labs-data/v2/types';
import {
  FAQService,
  sortQuestionsByCategoryAndOrder,
} from '../../utils/FAQService';
import { Category } from '@wix/ambassador-faq-category-v2-category/types';
import { ISettingsGetter, IWixStyleParams } from '@wix/tpa-settings';
import settingsParams, {
  IManageCategories,
  LayoutType,
} from './settingsParams';
import { presetToLayoutMap } from '../../hooks/presetToLayoutMap';
import { PresetSettings } from '../../types/designTypes';
import { CategoriesPosition } from '../../enums';
import constants from '../../constants';
import { getEditortype } from '../../utils/getEditortype';
import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import {
  getQuestionAPI,
  getListCategoriesAPI,
  getListQuestionsAPI,
  getAllQuestionsAPI,
} from './contollerAPIRequests';
import { draftjsToPlainText } from '../../utils/draftjsToPlainText';
import {
  CategoryId,
  GetListQuestions,
  loadPaginatedQuestionsUntilTarget,
} from './loadPaginatedQuestionsUntilTarget';

type GetQuestionIdResult = {
  categoryId: string | null | undefined;
  questionId: string | null | undefined;
  fromSearch: boolean;
};

const RETRY_DELAY_MS = 500;
const MAX_RETRY_ATTEMPTS = 2;
const RETRY_BACKOFF_MULTIPLIER = 2;

type SearchCacheItem = { entry: QuestionEntry; answerText: string };
const shouldShowOneListOfQuestions = (
  settingsGetter: { get: ISettingsGetter },
  styleParams: IWixStyleParams,
): boolean => {
  const showCategoriesSettingsParam =
    settingsGetter.get(settingsParams.displayContent) ===
    constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
  const showCategoriesStyleParam =
    styleParams.booleans.showFAQCategories ?? true;
  return !(showCategoriesSettingsParam && showCategoriesStyleParam);
};

const asyncRequestInitialData = async (
  settings: { get: any },
  styleParams: IWixStyleParams,
  api: FAQService,
  qcategoryId: string | null | undefined,
  deepLinkedQuestionId: string | null | undefined,
  withErrorHandler: any,
  httpClient: any,
  getListCategories: any,
  getListQuestions: GetListQuestions,
  getIsHaveQuestions: () => Promise<boolean>,
) => {
  let selectedCategory;
  const { showAll, categories } = settings.get(settingsParams.manageCategories);
  const { categories: categoriesList } = await getListCategories();
  const isHasQuestions = await getIsHaveQuestions();

  const showOneListOfQuestions = shouldShowOneListOfQuestions(
    settings as { get: ISettingsGetter },
    styleParams,
  );

  let firstId: string | undefined | null = qcategoryId ?? '';
  const validLists = categoriesList?.filter((category: Category) => {
    const categoryId = category?.id;
    if (showAll) {
      if (typeof firstId === 'undefined' || firstId === '') {
        firstId = categoryId;
      }
      return true;
    }
    if (!categoryId) {
      return false;
    }
    const isValid = categories?.includes(categoryId);
    if (isValid) {
      if (typeof firstId === 'undefined' || firstId === '') {
        firstId = categoryId;
      }
      return true;
    }
    return false;
  });
  const validCategories = validLists
    ?.filter((category: Category) => category?.id)
    .map((category: Category) => category?.id);
  if (showOneListOfQuestions) {
    selectedCategory = validCategories?.slice(0, 100);
  } else if (firstId) {
    selectedCategory = firstId;
  }
  const shouldPrefetchTarget =
    !showOneListOfQuestions ||
    !deepLinkedQuestionId ||
    !qcategoryId ||
    !Array.isArray(selectedCategory) ||
    selectedCategory.includes(qcategoryId);
  const questionEntries = await loadPaginatedQuestionsUntilTarget({
    categoryId: selectedCategory,
    targetQuestionId: shouldPrefetchTarget ? deepLinkedQuestionId : undefined,
    getListQuestions,
  });
  const sortedQuestionEntries = showOneListOfQuestions
    ? sortQuestionsByCategoryAndOrder(questionEntries, categoriesList)
    : questionEntries;

  return {
    selectedCategory,
    validCategories,
    questionEntries: sortedQuestionEntries,
    validLists,
    isHasQuestions,
  };
};

const createController: CreateControllerFn = async ({
  flowAPI,
  controllerConfig,
}: ControllerParams) => {
  // A module can host multiple FAQ widgets. Keep every mutable value scoped to
  // this controller so a second widget cannot overwrite the first one's state.
  let _api: FAQService;
  let _categoryId: string | null | undefined;
  let _styleParams = controllerConfig.config.style.styleParams;
  let _initialData:
    | Awaited<ReturnType<typeof asyncRequestInitialData>>
    | undefined;
  let isEditor3 = false;
  let categoriesSettings: IManageCategories;
  let categoriesToSearchIn: string[] | undefined;
  let searchCacheItems: SearchCacheItem[] | undefined;
  let searchCacheKey: string | undefined;

  const { isSEO = false, isEditor } = flowAPI.environment;

  const { httpClient, errorHandler, translations } = flowAPI;
  const { t } = translations;
  const { withErrorHandler, getResolvedError } = errorHandler;

  function getUuidCategoryIdFromUrl(url: string): string | null {
    const match = url.match(
      /[?&]categoryId=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:&|#|$)/,
    );
    return match ? match[1] : null;
  }

  function getUuidQuestionIdFromUrl(url: string): string | null {
    const match = url.match(
      /[?&]questionId=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:&|#|$)/,
    );
    return match ? match[1] : null;
  }

  const getQuestionId = async (): Promise<GetQuestionIdResult> => {
    try {
      const url = controllerConfig.wixCodeApi.location.url;
      const questionId = getUuidQuestionIdFromUrl(url);
      if (!questionId) {
        return {
          categoryId: null,
          questionId: null,
          fromSearch: false,
        };
      }
      const data = await withErrorHandler(
        () => getQuestionAPI({ id: questionId, httpClient }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('settings.error.invalid.data'),
              }),
            },
            serverError: () => ({
              message: t('settings.error.server.error'),
            }),
          },
        },
      );
      controllerConfig.setProps({
        questionIdReponse: data,
      });
      // TODO add error handler in the widget

      return {
        categoryId: data.questionEntries?.[0].categoryId,
        questionId: data.questionEntries?.[0].id,
        fromSearch: true,
      };
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchQuestionIdResponseError: resolvedError,
      });
      throw error;
    }
  };

  const getListCategories = async () => {
    try {
      const data = await withErrorHandler(
        () => getListCategoriesAPI(httpClient),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.invalid.data.error.handler'),
              }),
            },
            serverError: () => ({
              message: t('widget.server.error.handler'),
            }),
          },
        },
      );
      controllerConfig.setProps({
        listCategoriesResponse: data,
        fetchError: null,
      });
      return data;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchError: resolvedError,
      });
      throw error;
    }
  };
  let cachedCategories: { id: string; sortOrder?: number }[] | null = null;

  // Review: return sorted entries instead of mutating the accumulated response.
  const sortQuestionEntries = async (
    questionEntries: QuestionEntry[],
  ): Promise<QuestionEntry[]> => {
    const showOneListOfQuestions = shouldShowOneListOfQuestions(
      flowAPI.settings,
      _styleParams,
    );

    if (!showOneListOfQuestions) {
      return questionEntries;
    }

    let categories: { id: string; sortOrder?: number }[] = [];

    if (cachedCategories) {
      categories = cachedCategories;
    } else {
      const categoriesData = await getListCategories();
      categories = (categoriesData.categories || [])
        .filter((category: Category) => category.id)
        .map((category: Category) => ({
          id: category.id!,
          sortOrder: category.sortOrder ?? undefined,
        }));
      cachedCategories = categories;
    }

    // Sort questions by category and sortOrder
    if (categories.length > 0) {
      return (
        sortQuestionsByCategoryAndOrder(questionEntries, categories) ||
        questionEntries
      );
    }

    return questionEntries;
  };

  const getListQuestions = async ({
    categoryId,
    cursor,
    limit,
    prev = [],
    suppressError = false,
  }: {
    categoryId?: CategoryId;
    cursor?: string;
    limit?: number;
    prev?: QuestionEntry[];
    suppressError?: boolean;
  }) => {
    try {
      const data = await withErrorHandler(
        () =>
          getListQuestionsAPI({ categoryId, httpClient, cursor, limit, isSEO }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.list.questions.invalid.data.error'),
              }),
            },
            serverError: () => ({
              message: t('widget.list.questions.server.error'),
            }),
          },
        },
      );

      const questionEntries = await sortQuestionEntries([
        ...prev,
        ...(data.questionEntries || []),
      ]);
      controllerConfig.setProps({
        listQuestionsResponse: {
          ...data,
          questionEntries,
        },
        initialData: {
          questionEntries,
          pagingMetadata: data.pagingMetadata,
        },
        fetchError: null,
      });
      return data;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      if (!suppressError) {
        controllerConfig.setProps({
          fetchError: resolvedError,
        });
      }
      throw error;
    }
  };

  const getIsHaveQuestions = async () => {
    try {
      const data = await withErrorHandler(
        () => getListQuestionsAPI({ httpClient, limit: 1, isSEO }),
        {
          errorCodesMap: {
            applicationError: {
              INVALID_DATA: () => ({
                message: t('widget.list.questions.invalid.data.error'),
              }),
            },
            serverError: () => ({
              message: t('widget.list.questions.server.error'),
            }),
          },
        },
      );

      // Don't setProps here; isHasQuestions is passed once in onPageReady(allProps) to avoid extra Widget re-renders.
      return (data.questionEntries && data.questionEntries.length > 0) ?? false;
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchError: resolvedError,
      });
      throw error;
    }
  };

  const getSearchTerm = async ({
    term,
  }: {
    cursor?: any;
    term: any;
    prev?: QuestionEntry[];
  }) => {
    try {
      if (!term) {
        controllerConfig.setProps({
          searchTermResponse: null,
        });
        const initialData = await getInitialDataWithRetry(
          flowAPI.settings,
          _api,
          _categoryId,
          false,
        );
        controllerConfig.setProps({
          initialData,
        });
        return;
      }

      const cacheKey = JSON.stringify(categoriesToSearchIn ?? null);
      if (!searchCacheItems || searchCacheKey !== cacheKey) {
        const entries = await withErrorHandler(
          () =>
            getAllQuestionsAPI({
              categoryIds: categoriesToSearchIn,
              httpClient,
            }),
          {
            errorCodesMap: {
              applicationError: {
                INVALID_DATA: () => ({
                  message: t('widget.invalid.data.error.handler'),
                }),
              },
              serverError: () => ({
                message: t('widget.server.error.handler'),
              }),
            },
          },
        );
        searchCacheKey = cacheKey;
        searchCacheItems = (entries ?? []).map((entry) => ({
          entry,
          answerText:
            draftjsToPlainText(entry.draftjs) || entry.plainText || '',
        }));
      }

      const lowerTerm = String(term).trim().toLowerCase();
      if (!lowerTerm) {
        controllerConfig.setProps({ searchTermResponse: null });
        const initialData = await getInitialDataWithRetry(
          flowAPI.settings,
          _api,
          _categoryId,
          false,
        );
        controllerConfig.setProps({ initialData });
        return;
      }
      const questionEntries = searchCacheItems
        .filter(
          (item) =>
            item.entry.question?.toLowerCase().includes(lowerTerm) ||
            item.answerText.toLowerCase().includes(lowerTerm),
        )
        .map((item) => item.entry);

      controllerConfig.setProps({
        searchTermResponse: {
          questionEntries,
          pagingMetadata: { hasNext: false, cursors: {} },
        },
        initialData: {
          questionEntries,
          pagingMetadata: { hasNext: false },
          validCategories: null,
          selectedCategory: null,
          validLists: null,
        },
      });
    } catch (error) {
      const resolvedError = getResolvedError(error);
      controllerConfig.setProps({
        fetchSearchTermError: resolvedError,
      });
      throw error;
    }
  };

  const getCategoryIdFromURL = () => {
    const url = controllerConfig.wixCodeApi.location.url;
    const categoryId = getUuidCategoryIdFromUrl(url);

    return {
      categoryId,
      fromSearch: categoryId !== null,
    };
  };

  const getInitialData = async (
    settings: { get: ISettingsGetter },
    faqService: FAQService,
    categoryId: string | null | undefined,
    deepLinkedQuestionId?: string | null,
  ) => {
    let result;
    try {
      result = await asyncRequestInitialData(
        settings,
        _styleParams,
        faqService,
        categoryId,
        deepLinkedQuestionId,
        withErrorHandler,
        httpClient,
        getListCategories,
        getListQuestions,
        getIsHaveQuestions,
      );
    } catch (e) {
      result = {
        validLists: null,
        selectedCategory: null,
        validCategories: null,
        questionEntries: [],
        isHasQuestions: false,
      };
    }
    return result;
  };

  const getInitialDataWithRetry = async (
    settings: { get: ISettingsGetter },
    faqService: FAQService,
    categoryId: string | null | undefined,
    shouldRetry: boolean,
    deepLinkedQuestionId?: string | null,
  ) => {
    let initialData = await getInitialData(
      settings,
      faqService,
      categoryId,
      deepLinkedQuestionId,
    );

    // Retry mechanism with exponential backoff for editor environment
    // to handle race conditions during initial data load
    if (
      shouldRetry &&
      (!initialData.validLists || initialData.validLists.length === 0) &&
      (!initialData.validCategories || initialData.validCategories.length === 0)
    ) {
      for (
        let retryAttempt = 0;
        retryAttempt < MAX_RETRY_ATTEMPTS;
        retryAttempt++
      ) {
        const delay =
          RETRY_DELAY_MS * Math.pow(RETRY_BACKOFF_MULTIPLIER, retryAttempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
        initialData = await getInitialData(
          settings,
          faqService,
          categoryId,
          deepLinkedQuestionId,
        );
        // Break early if we successfully retrieved data
        if (
          initialData.validLists &&
          initialData.validLists.length > 0 &&
          initialData.validCategories &&
          initialData.validCategories.length > 0
        ) {
          break;
        }
      }
    }

    return initialData;
  };

  const onPageReady = async () => {
    try {
      const { appParams, wixCodeApi, platformAPIs, config } = controllerConfig;
      _styleParams = config.style.styleParams;
      const metaSiteId = platformAPIs?.bi?.metaSiteId;
      const { instance, instanceId } = appParams;
      const { url, baseUrl: siteBaseUrl } = wixCodeApi.location;

      const baseURL = url;
      _api = new FAQService(
        instance,
        instanceId,
        wixCodeApi?.window?.multilingual,
      );

      let questionIdResult: GetQuestionIdResult;

      try {
        questionIdResult = await getQuestionId();
      } catch (error) {
        console.warn('Failed to fetch question ID:', error);
        questionIdResult = {
          categoryId: null,
          questionId: null,
          fromSearch: false,
        };
      }

      let { questionId } = questionIdResult;
      let { categoryId, fromSearch } = questionIdResult;
      if (!categoryId && isSEO) {
        const { categoryId: categoryIdFromURL, fromSearch: fromSearchFromURL } =
          getCategoryIdFromURL();
        if (categoryIdFromURL) {
          categoryId = categoryIdFromURL;
          fromSearch = fromSearchFromURL;
        }
      }
      _categoryId = categoryId;

      const initialData = await getInitialDataWithRetry(
        flowAPI.settings,
        _api,
        categoryId,
        isEditor,
        questionId,
      );
      if (initialData.validLists?.length !== 0 && !questionId) {
        const showFirstAnswer = flowAPI.settings.get(
          settingsParams.openFirstAnswer,
        );
        if (showFirstAnswer && initialData.questionEntries?.length) {
          questionId = initialData.questionEntries?.[0].id;
        }
      }
      if (_styleParams?.booleans?.isEditor3) {
        // Editor3 boolean param is set
        isEditor3 = _styleParams?.booleans?.isEditor3;
      } else {
        isEditor3 = config.publicData.APP?.isEditor3 ?? false;
      }

      const allProps = {
        instance,
        instanceId,
        baseURL,
        siteBaseUrl,
        questionId,
        initialData,
        fromSearch,
        api: {
          getListCategories,
          getListQuestions,
          getSearchTerm,
          scrollToElement: wixCodeApi.window.scrollToElement,
          metaSiteId,
        },
        fitToContentHeight: true,
        isSEO,
        isEditor3,
        isHasQuestions: initialData.isHasQuestions,
      };

      controllerConfig.setProps(allProps);
      _initialData = initialData;
      if (isSEO && initialData.questionEntries?.length) {
        const structuredData = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: initialData.questionEntries.map(
            (question: QuestionEntry) => ({
              '@type': 'Question',
              name: question.question,
              acceptedAnswer: {
                '@type': 'Answer',

                text: question.plainText,
              },
            }),
          ),
        };

        const biLogger = flowAPI.bi;
        await controllerConfig.wixCodeApi.seo
          .setStructuredData([structuredData])
          .catch((error) => {
            biLogger?.log({
              paramsData: {
                error: error.message,
              },
              biType: 'structuredDataError',
            });
          });
      }
    } catch (error) {
      console.warn('Error in onPageReady:', error);
    }
  };

  const reportFaqAppStartLoading = (
    commonConfig: { host?: string } | null,
  ): void => {
    const biLogger = flowAPI.bi;
    try {
      const isResponsive = _styleParams?.booleans?.responsive ?? false;
      const editortype = getEditortype(isEditor3, isResponsive, commonConfig);
      const event = faqAppStartLoading({
        isOoi: true,
        editortype,
      });
      (biLogger?.report as (e: typeof event) => void)?.(event);
    } catch (e) {
      console.warn('biLogger report faqAppStartLoading failed', e);
    }
  };

  const reportFaqAppLoads = (commonConfig: { host?: string } | null): void => {
    if (!_initialData) {
      return;
    }
    const biLogger = flowAPI.bi;
    try {
      const isResponsive = _styleParams?.booleans?.responsive ?? false;
      const editortype = getEditortype(isEditor3, isResponsive, commonConfig);
      let layout_type: LayoutType =
        flowAPI.settings.get(settingsParams.layout) ?? LayoutType.Collapse;
      let category_placement: CategoriesPosition =
        flowAPI.settings.get(settingsParams.categoriesPosition) ??
        CategoriesPosition.TOP;
      if (isEditor3) {
        const preset =
          (_styleParams?.numbers?.presetSettings as PresetSettings) ??
          PresetSettings.ACCORDION_TOP_CATEGORIES;
        const mapped = presetToLayoutMap[preset];
        layout_type = mapped.layout;
        category_placement = mapped.categoriesPosition;
      }
      const event = faqAppLoads({
        num_of_categories_shown: _initialData.validLists?.length ?? 0,
        num_of_categories:
          flowAPI.settings.get(settingsParams.manageCategories)?.categories
            ?.length ?? 0,
        num_of_questions: _initialData.questionEntries?.length ?? 0,
        layout_type,
        columns: flowAPI.settings.get(settingsParams.structure),
        category_placement,
        isOoi: true,
        editortype,
      } as faqAppLoadsParams & { isOoi: boolean; editortype: string });
      (biLogger?.report as (e: typeof event) => void)?.(event);
    } catch (e) {
      console.warn('biLogger report faqAppLoads failed', e);
    }
  };

  return {
    async pageReady($w, wixAPI) {
      _initialData = undefined;
      const query = wixAPI?.location?.query ?? {};
      try {
        categoriesSettings = (flowAPI.settings.get(
          settingsParams.manageCategories,
        ) as IManageCategories | undefined) || {
          showAll: true,
          categories: [],
        };
        categoriesToSearchIn = categoriesSettings.showAll
          ? undefined
          : categoriesSettings.categories;
      } catch (error) {
        categoriesSettings = {
          showAll: true,
          categories: [],
        };
        categoriesToSearchIn = undefined;
      }
      let commonConfig: { host?: string } | null = null;
      if (query.commonConfig) {
        try {
          commonConfig = JSON.parse(query.commonConfig) as { host?: string };
        } catch (e) {
          commonConfig = null;
        }
      }

      reportFaqAppStartLoading(commonConfig);
      const result = await onPageReady();
      reportFaqAppLoads(commonConfig);
      return result;
    },
    async updateAppSettings() {
      return onPageReady();
    },
  };
};

export default createController;
